import React, { useEffect, useRef, useState } from 'react';
import { Check, Loader, Leaf, Plus, Minus, ChevronLeft } from 'lucide-react';

/**
 * The customer's side of the lawn plans.
 *
 * The owner, Sep 17 2026: show the three plans as recommendations; when they
 * tap one they can add or drop anything and the price follows the cost sheet;
 * what they send comes to him to approve.
 *
 * Built for the person reading it on a phone in their driveway: three cards,
 * plain words, one number that moves as they tap, and one button. Nothing here
 * books or charges anything — the last screen says so.
 *
 * Used on their own page (portal token), on a bid, and on a public link.
 */

type Api = { get: (u: string, c?: any) => Promise<any>; post: (u: string, b?: any) => Promise<any> };

const money = (n: any) => `$${Number(n || 0).toLocaleString('en-US')}`;

export default function PlanPicker({ api, token, quoteId, compact = false, onSent }: {
  api: Api; token?: string | null; quoteId?: number | null; compact?: boolean; onSent?: () => void;
}) {
  const [menu, setMenu] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // What they tell us when we don't already know the yard.
  // A link can carry the lawn it is for — /plans?sqft=2500&grass=sa — so the person it is texted to sees their own prices without typing anything.
  const fromLink = (k: string, ok: RegExp) => { try { const v = new URLSearchParams(window.location.search).get(k) || ''; return ok.test(v) ? v : ''; } catch { return ''; } };
  const [sqft, setSqft] = useState(() => fromLink('sqft', /^\d{3,6}$/));
  const [grass, setGrass] = useState(() => fromLink('grass', /^(sa|bz)$/));
  const [mow, setMow] = useState<boolean | null>(null);

  const [chosen, setChosen] = useState<string | null>(null);
  const [items, setItems] = useState<Record<string, number>>({});
  const [priced, setPriced] = useState<any>(null);
  const [note, setNote] = useState('');
  const [who, setWho] = useState({ name: '', phone: '', email: '', address: '' });
  const [sent, setSent] = useState<any>(null);
  const [sending, setSending] = useState(false);

  const params = () => ({
    ...(token ? { token } : {}),
    ...(sqft ? { sqft } : {}),
    ...(grass ? { grass } : {}),
    ...(mow === null ? {} : { mow: mow ? '1' : '0' }),
  });

  // Everything re-prices together whenever the yard changes — typing a size or
  // picking the grass. Doing this in an effect (rather than on blur) is what
  // keeps the cards and the builder showing the same number.
  const [touched, setTouched] = useState(false);
  useEffect(() => {
    let dropped = false;
    const run = async () => {
      try {
        const { data } = await api.get('/plans/menu', { params: { ...(token ? { token } : {}), ...(sqft ? { sqft } : {}), ...(grass ? { grass } : {}), ...(mow === null ? {} : { mow: mow ? '1' : '0' }) } });
        if (dropped) return;
        setMenu(data);
        if (data.grass && !grass) setGrass(data.grass);
        if (data.sqft && !sqft) setSqft(String(data.sqft));
        if (mow === null) setMow(!!data.mow);
        setError(null);
        // Their own changes are theirs; an untouched plan follows the new price.
        if (chosen && !touched) {
          const plan = (data.plans || []).find((p: any) => p.plan === chosen);
          if (plan) { setItems({ ...(plan.items || {}) }); setPriced({ plan: chosen, plan_name: plan.name, monthly: plan.monthly, yearly: plan.yearly, changed: false }); }
        }
      } catch {
        if (!dropped) setError('We could not load the plans just now.');
      } finally { if (!dropped) setLoading(false); }
    };
    const t = setTimeout(run, menu ? 400 : 0);
    return () => { dropped = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, sqft, grass, mow]);

  // The price follows every tap, worked out by the same sheet the office uses.
  const timer = useRef<any>(null);
  useEffect(() => {
    if (!chosen) return;
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        const { data } = await api.post('/plans/price', { ...params(), plan: chosen, items, mow: mow !== false });
        setPriced(data);
      } catch { /* the last price stays on screen */ }
    }, 250);
    return () => clearTimeout(timer.current);
  }, [chosen, items, mow]); // eslint-disable-line react-hooks/exhaustive-deps

  const serviceMenu: any[] = menu?.menu || [];
  const known = !!menu?.sqft_known;
  const needAnswers = !known && !sqft;

  function choose(planKey: string) {
    const plan = (menu?.plans || []).find((p: any) => p.plan === planKey);
    setTouched(false);
    setItems({ ...(plan?.items || {}) });
    setChosen(planKey);
    setPriced({ plan: planKey, plan_name: plan?.name, monthly: plan?.monthly, yearly: plan?.yearly, changed: false });
  }

  const step = (service: string, by: number) => {
    setTouched(true);
    setItems(prev => {
      const next = { ...prev, [service]: Math.max(0, Math.min(24, (prev[service] || 0) + by)) };
      if (!next[service]) delete next[service];
      return next;
    });
  };

  async function send() {
    // Never a dead button: say what is missing rather than sitting there greyed out.
    if (!token && !who.phone.trim() && !who.email.trim()) {
      setError('Leave a mobile or an email so Michael can get back to you.');
      return;
    }
    setError(null);
    setSending(true);
    try {
      const { data } = await api.post('/plans/request', {
        ...params(), plan: chosen, items, note: note.trim() || undefined,
        quote_id: quoteId || undefined,
        ...(token ? {} : { name: who.name, phone: who.phone, email: who.email, address: who.address }),
      });
      setSent(data);
      onSent?.();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'That did not send. Text us and we will sort it.');
    } finally { setSending(false); }
  }

  if (loading) return <div className="py-8 flex justify-center"><Loader className="h-5 w-5 animate-spin text-brand-600" /></div>;

  if (sent) {
    return (
      <div className="rounded-3xl border-2 border-emerald-300 bg-emerald-50 shadow-soft p-5 text-center">
        <Check className="h-8 w-8 text-emerald-600 mx-auto mb-2" />
        <p className="font-semibold text-emerald-900">{sent.message}</p>
        <p className="text-sm text-emerald-800 mt-1">Nothing is booked or charged yet.</p>
      </div>
    );
  }

  // ── The three recommendations ──────────────────────────────────────────────
  if (!chosen) {
    return (
      <div className="space-y-3">
        {!compact && (
          <div>
            <h2 className="text-lg font-black uppercase tracking-tight text-gray-900">Three plans for your lawn</h2>
            <p className="text-sm text-gray-600">
              {known
                ? <>Priced for your yard{menu?.sqft ? <>, {Number(menu.sqft).toLocaleString('en-US')} sq ft of turf</> : null}. Pick one, then change anything you like.</>
                : 'Tell us roughly how big the lawn is and we will price all three.'}
            </p>
          </div>
        )}

        {/* Grass is asked for even when we know the size: without it the price
            assumes the dearer grass and Bermuda-only extras (rye) stay hidden. */}
        {(!known || !grass) && (
          <div className="rounded-3xl border border-brand-100 bg-white shadow-soft p-4 grid grid-cols-2 gap-3">
            {!known && (
              <label className="text-xs text-gray-600 col-span-2 sm:col-span-1">How big is the lawn?
                <input type="number" inputMode="numeric" value={sqft} onChange={e => setSqft(e.target.value)}
                  placeholder="square feet of grass" className="mt-1 w-full border-2 border-brand-200 rounded-2xl px-3 min-h-[48px] text-[16px]" />
                <span className="block text-[11px] text-gray-500 mt-0.5">Not sure? A quarter-acre lot is about 6,000. We measure it properly before anything starts.</span>
              </label>
            )}
            <label className={`text-xs text-gray-600 ${known ? 'col-span-2' : ''}`}>Which grass?
              <select value={grass} onChange={e => setGrass(e.target.value)}
                className="mt-1 w-full border-2 border-brand-200 rounded-2xl px-3 min-h-[48px] text-[16px]">
                <option value="">Not sure, we will check</option>
                <option value="bz">Bermuda or Zoysia</option>
                <option value="sa">St. Augustine</option>
              </select>
            </label>
          </div>
        )}

        {/* The no-mow option: treatments without our mowing cost half again. */}
        {mow !== null && (
          <label className="flex items-center gap-2 text-xs text-gray-600 px-1">
            <input type="checkbox" checked={mow === false} onChange={e => setMow(e.target.checked ? false : true)} />
            We don't mow this lawn — treatments only
          </label>
        )}

        {error && <p className="text-sm text-red-600">{error}</p>}

        {!known && !sqft && (
          <p className="text-xs text-gray-500">
            These are for a typical {Number(menu?.default_sqft || 6000).toLocaleString('en-US')} sq ft lawn.
            Put your own size in above and all three re-price.
          </p>
        )}

        {(menu?.plans || []).map((p: any) => (
          <button key={p.plan} onClick={() => choose(p.plan)} disabled={needAnswers}
            className={`press w-full text-left rounded-3xl border-2 p-5 shadow-soft disabled:opacity-50 ${p.recommended ? 'border-brand-500 bg-brand-100 shadow-lift' : 'border-brand-100 bg-white hover:border-brand-300'}`}>
            <div className="flex items-baseline justify-between gap-2">
              <span className="font-bold text-gray-900 text-lg">{p.name}</span>
              {menu?.current?.plan === p.plan && (
                <span className="text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5 whitespace-nowrap">
                  You're on this one{menu.current.monthly ? `, ${money(menu.current.monthly)}/mo now` : ''}
                </span>
              )}
              <span className="text-right">
                <span className="text-2xl font-bold text-gray-900">{money(p.monthly)}</span>
                <span className="text-sm text-gray-500"> / mo</span>
              </span>
            </div>
            <p className="text-xs text-gray-500">{p.kicker} · {p.visits} applications a year · {money(p.yearly)} a year</p>
            <p className="text-sm text-gray-700 mt-2">
              {Object.entries(p.items).map(([k, n]: any) => `${n} ${(serviceMenu.find((m: any) => m.service === k)?.word) || k}`).join(' · ')}
            </p>
            {p.recommended && <p className="mt-2 text-xs font-semibold text-brand-700">Most lawns are on this one</p>}
          </button>
        ))}
        {!known && !needAnswers && <p className="text-[11px] text-gray-500">Prices are for {Number(menu?.sqft || 0).toLocaleString('en-US')} sq ft{grass ? '' : ', and assume the grass that costs more to treat'}. Plus tax. We confirm after we measure.</p>}
      </div>
    );
  }

  // ── Their own mix ──────────────────────────────────────────────────────────
  const rows = serviceMenu.filter(m => m.service !== 'rye' || grass !== 'sa');
  const inPlan = rows.filter(m => (items[m.service] || 0) > 0);
  const rest = rows.filter(m => !(items[m.service] || 0));

  return (
    <div className="space-y-3">
      <button onClick={() => { setChosen(null); setPriced(null); }} className="text-sm text-gray-500 inline-flex items-center gap-1">
        <ChevronLeft className="h-4 w-4" /> All three plans
      </button>

      <div className="rounded-3xl border-2 border-brand-300 bg-brand-100 shadow-soft p-5 flex items-baseline justify-between gap-3">
        <div>
          <p className="font-bold text-gray-900">{priced?.plan_name || menu?.plans?.find((p: any) => p.plan === chosen)?.name}</p>
          <p className="text-xs text-gray-600">{priced?.changed ? 'Your own mix' : 'As recommended'} · {money(priced?.yearly)} a year + tax</p>
        </div>
        <p className="text-3xl font-bold text-gray-900 whitespace-nowrap">{money(priced?.monthly)}<span className="text-sm font-normal text-gray-500"> / mo</span></p>
      </div>

      <p className="text-xs text-gray-500 px-1">
        {Number(sqft || menu?.sqft) > 0
          ? <>Priced for {Number(sqft || menu?.sqft).toLocaleString()} sq ft of turf</>
          : <>Priced for a typical lawn — we measure before anything starts</>}
        {grass === 'sa' ? ' · St. Augustine' : grass === 'bz' ? ' · Bermuda or Zoysia' : ''}
        {mow === false ? ' · treatments only' : ''} ·{' '}
        <button onClick={() => { setChosen(null); setPriced(null); }} className="underline">change</button>
      </p>

      <div className="rounded-3xl border border-brand-100 bg-white shadow-soft divide-y divide-brand-50">
        {inPlan.map(m => (
          <Row key={m.service} m={m} count={items[m.service] || 0} onStep={step} />
        ))}
      </div>

      {rest.length > 0 && (
        <details className="rounded-3xl border border-brand-100 bg-white shadow-soft p-4">
          <summary className="text-sm font-semibold text-gray-800 cursor-pointer">Add something else</summary>
          <div className="mt-2 divide-y divide-gray-100">
            {rest.map(m => <Row key={m.service} m={m} count={0} onStep={step} />)}
          </div>
        </details>
      )}

      <textarea value={note} onChange={e => setNote(e.target.value)} rows={2}
        placeholder="Anything we should know? (the dog, the gate, a patch that always struggles)"
        className="w-full border-2 border-brand-200 rounded-2xl px-3 py-3 text-[16px]" />

      {!token && (
        <div className="grid grid-cols-2 gap-2">
          <p className="col-span-2 text-xs text-gray-500">Where do we reach you? A mobile or an email is enough.</p>
          <input value={who.name} onChange={e => setWho({ ...who, name: e.target.value })} placeholder="Your name" className="border border-gray-300 rounded-lg px-3 py-2 text-sm col-span-2" />
          <input value={who.phone} onChange={e => setWho({ ...who, phone: e.target.value })} placeholder="Mobile" className="border border-gray-300 rounded-lg px-3 py-2 text-sm" />
          <input value={who.email} onChange={e => setWho({ ...who, email: e.target.value })} placeholder="Email" className="border border-gray-300 rounded-lg px-3 py-2 text-sm" />
          <input value={who.address} onChange={e => setWho({ ...who, address: e.target.value })} placeholder="Address of the lawn" className="border border-gray-300 rounded-lg px-3 py-2 text-sm col-span-2" />
        </div>
      )}

      {error && <p className="text-sm text-red-600">{error}</p>}

      <button onClick={send} disabled={sending}
        className="press w-full py-3.5 rounded-xl bg-brand-600 text-black font-semibold disabled:opacity-50">
        {sending ? 'Sending…' : `${menu?.current ? 'Send this change to' : 'Send this to'} Michael, ${money(priced?.monthly)} a month`}
      </button>
      <p className="text-[11px] text-gray-500 text-center">
        He confirms it before anything is booked, and nothing is charged until a visit is done.
        No contract. Pause any time.
      </p>
    </div>
  );
}

function Row({ m, count, onStep }: { m: any; count: number; onStep: (s: string, by: number) => void }) {
  return (
    <div className="flex items-start gap-3 py-3 px-3">
      <Leaf className={`h-4 w-4 mt-0.5 flex-shrink-0 ${count ? 'text-emerald-600' : 'text-gray-300'}`} />
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-semibold capitalize ${count ? 'text-gray-900' : 'text-gray-500'}`}>{m.word}</p>
        <p className="text-xs text-gray-500">{m.help}</p>
        <p className="text-[11px] text-gray-500 mt-0.5">{money(m.per_visit)} a visit</p>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <button onClick={() => onStep(m.service, -1)} disabled={!count}
          className="h-8 w-8 rounded-full border border-gray-300 text-gray-600 flex items-center justify-center disabled:opacity-30" aria-label={`One fewer ${m.word}`}>
          <Minus className="h-4 w-4" />
        </button>
        <span className="w-6 text-center text-sm font-semibold text-gray-900">{count}</span>
        <button onClick={() => onStep(m.service, 1)}
          className="h-8 w-8 rounded-full border border-brand-400 text-brand-700 flex items-center justify-center" aria-label={`One more ${m.word}`}>
          <Plus className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
